/**
 * 地図の表示範囲を自動調整するフック（Leafletの map.fitBounds 相当）
 *
 * Google Maps の fitBounds には maxZoom オプションが無いため、
 * フィット完了（idle）後にズームを丸めて上限を再現する。
 */

import { useMap, useMapsLibrary } from '@vis.gl/react-google-maps'
import { useEffect, useRef } from 'react'

/** fitKey 未指定時に「初回のみフィット」を表すための番兵 */
const INITIAL_FIT_KEY = '__initial__'

type UseFitBoundsOptions = {
  points: google.maps.LatLngLiteral[]
  /** 余白（ピクセル）。数値で全方向、オブジェクトで個別指定 */
  padding?: number | google.maps.Padding
  maxZoom?: number
  /**
   * 値が変わったときのみ再フィットするキー。
   * 未指定なら初回に有効データが揃った時だけフィットし、以降はユーザーの視点を維持する。
   */
  fitKey?: string | number
}

export function useFitBounds({ points, padding = 20, maxZoom, fitKey }: UseFitBoundsOptions): void {
  const map = useMap()
  const coreLibrary = useMapsLibrary('core')
  const lastFitKeyRef = useRef<string | number | null>(null)

  useEffect(() => {
    if (!map || !coreLibrary) return
    if (points.length === 0) return

    const currentKey = fitKey ?? INITIAL_FIT_KEY
    if (lastFitKeyRef.current === currentKey) return

    let clampListener: google.maps.MapsEventListener | null = null

    /** 描画領域が確定する前に呼ぶと誤った中心・ズームで固定されるため、サイズを確認してから実行する */
    const fit = () => {
      const div = map.getDiv()
      if (div.offsetWidth === 0 || div.offsetHeight === 0) return false

      const bounds = new coreLibrary.LatLngBounds()
      points.forEach((point) => bounds.extend(point))
      map.fitBounds(bounds, padding)
      lastFitKeyRef.current = currentKey

      if (maxZoom === undefined) return true

      const listener = map.addListener('idle', () => {
        listener.remove()
        const zoom = map.getZoom()
        if (zoom !== undefined && zoom > maxZoom) map.setZoom(maxZoom)
      })
      clampListener = listener
      return true
    }

    if (fit()) return () => clampListener?.remove()

    // 非表示やレイアウト確定前でサイズが 0 のときは、確定してから改めてフィットする
    const observer = new ResizeObserver(() => {
      if (fit()) observer.disconnect()
    })
    observer.observe(map.getDiv())

    return () => {
      observer.disconnect()
      clampListener?.remove()
    }
  }, [map, coreLibrary, points, padding, maxZoom, fitKey])
}
